import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { CreateRoleInput } from '@eombor/contracts';
import { Button, Dialog, Input } from '@eombor/ui';
import { zodResolver } from '../../../shared/form/zod-resolver.js';
import { t } from '../../../shared/i18n/index.js';
import type { RoleDto } from '../api/rbac.api.js';

/**
 * Forma sxemasi `@eombor/contracts` dagi `createRoleSchema` bilan bir xil
 * qoidalarda, lekin xabarlari maydon yonida turishga mo'ljallangan —
 * `features/auth/model/login-form.ts` dagi qoidaning davomi.
 */
export const roleFormSchema = z.object({
  code: z.string().regex(/^[a-z0-9-]{3,60}$/, t('rbac.errorCode')),
  name: z.string().min(3, t('rbac.errorName')).max(150),
  description: z.string().max(1000),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;

/** Forma qiymatlari backend kutgan tanaga mosligi tur darajasida qulflanadi. */
type Assert<T extends true> = T;
export type RoleFormMatchesContract = Assert<RoleFormValues extends CreateRoleInput ? true : false>;

export interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  /** Berilsa — tahrirlash rejimi; kod o'zgarmaydi (backendda ham `omit`). */
  role?: RoleDto | null;
  onSubmit: (values: RoleFormValues) => void;
  pending?: boolean;
  /** Serverdan kelgan xato (masalan, kod band) — forma tepasida ko'rinadi. */
  error?: string | null;
}

const EMPTY: RoleFormValues = { code: '', name: '', description: '' };

export const RoleDialog = ({
  open,
  onClose,
  role = null,
  onSubmit,
  pending = false,
  error = null,
}: RoleDialogProps) => {
  const editing = role !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: EMPTY,
  });

  // Oyna har ochilganda forma tanlangan roldan qayta to'ldiriladi — oldingi
  // rolning nomi yangi oynada qolib ketmaydi.
  useEffect(() => {
    if (!open) return;
    reset(
      role === null
        ? EMPTY
        : { code: role.code, name: role.name, description: role.description ?? '' },
    );
  }, [open, role, reset]);

  const submit = handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('rbac.dialogEditTitle') : t('rbac.dialogCreateTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('rbac.cancel')}
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            {editing ? t('rbac.save') : t('rbac.create')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {error !== null ? (
          <p
            role="alert"
            className="rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        ) : null}

        <Input
          label={t('rbac.fieldCode')}
          mono
          autoComplete="off"
          spellCheck={false}
          disabled={editing}
          hint={editing ? undefined : t('rbac.fieldCodeHint')}
          error={errors.code?.message}
          {...register('code')}
        />

        <Input
          label={t('rbac.fieldName')}
          autoComplete="off"
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label={t('rbac.fieldDescription')}
          autoComplete="off"
          hint={t('rbac.fieldDescriptionHint')}
          error={errors.description?.message}
          {...register('description')}
        />

        {/* Klaviatura bilan `Enter` bosilganda forma yuborilsin — tugma
            ko'rinmaydi, chunki haqiqiy tugmalar pastki qatorda turadi. */}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
          {t('rbac.save')}
        </button>
      </form>
    </Dialog>
  );
};
